// @ts-strict-ignore
import Card from 'antd/lib/card';
import Drawer from 'antd/lib/drawer';
import Space from 'antd/lib/space';
import React from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../reducers/reducers';
import { selectAppTimeFormatSetting, selectEffectiveTimezone } from '../../selectors/selectors';
import { CommitItem, IntegrationType, StabilityIssue } from '../../types';
import IntegrationIcon from '../icons/IntegrationIcon';
import { drawerCardHeadStyles, drawerCardBodyStyles, antGrey, getAvatarColorForName } from '../styles/globalStyles';
import Text from 'antd/lib/typography/Text';
import Paragraph from 'antd/lib/typography/Paragraph';
import Link from 'antd/lib/typography/Link';
import Avatar from 'antd/lib/avatar';
import Collapse from 'antd/lib/collapse';
import { formatDateWithTimezone } from '../../timeHelpers';
import commitNode from '../../../assets/commit-node.svg';

const StabilityIssueDrawer: React.FC<{
    open: boolean;
    onClose: () => void;
    integrationId?: IntegrationType;
    issue?: StabilityIssue;
    vcsId?: IntegrationType;
    appId: string;
}> = (props) => {
    const use24HourTimeFormat = useSelector((state: RootState) => selectAppTimeFormatSetting(state, props.appId));
    const effectiveTimezone = useSelector((state: RootState) => selectEffectiveTimezone(state, props.appId));
    return (
        <Drawer
            open={props.open}
            placement="right"
            title={null}
            width={550}
            bodyStyle={{ padding: 0, paddingBottom: 85 }}
            closable={false}
            onClose={props.onClose}
        >
            {props.issue && (
                <Card
                    title={
                        <Space align="center">
                            <IntegrationIcon integrationId={props.integrationId} />
                            <Text style={{ fontSize: 14 }}>Issue detail</Text>
                        </Space>
                    }
                    headStyle={{
                        ...drawerCardHeadStyles,
                    }}
                    bodyStyle={{
                        ...drawerCardBodyStyles,
                    }}
                    bordered={false}
                >
                    <Link href={props.issue.url} target="_blank" strong ellipsis>
                        {props.issue.crashShortDescription}
                    </Link>
                    <Paragraph
                        style={{ color: antGrey.grey8, marginTop: 12 }}
                        ellipsis={{ rows: 3, expandable: true, symbol: 'more' }}
                    >
                        {props.issue.crashLongDescription}
                    </Paragraph>
                    {props.issue.blameFile && props.issue.blameLine && (
                        <div style={{ marginBottom: 14 }}>
                            <Text code>
                                {props.issue.blameFile}:{props.issue.blameLine}
                            </Text>
                        </div>
                    )}
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: 8,
                        }}
                    >
                        <Text style={{ color: antGrey.grey7 }}>Occurrences (Release / Total)</Text>{' '}
                        <Text style={{ color: antGrey.grey7 }}>
                            {props.issue.occurrencesCountInRelease} / {props.issue.totalOccurrencesCount}
                        </Text>
                    </div>
                    {props.vcsId && props.issue.associatedCommits?.length > 0 && (
                        <Collapse ghost defaultActiveKey={'1'}>
                            <Collapse.Panel
                                header={
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <img src={commitNode} />
                                        <Text>Suspect commits ({props.issue.associatedCommits.length})</Text>
                                    </div>
                                }
                                key="1"
                            >
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                                    {props.issue.associatedCommits.map((commit) => {
                                        return (
                                            <CommitCard
                                                key={commit.identifier}
                                                commit={commit}
                                                vcsId={props.vcsId}
                                                use24HourTimeFormat={use24HourTimeFormat}
                                                effectiveTimezone={effectiveTimezone}
                                            />
                                        );
                                    })}
                                </div>
                            </Collapse.Panel>
                        </Collapse>
                    )}
                    {props.issue.stackTrace && props.issue.stackTrace.length > 0 && (
                        <Collapse ghost defaultActiveKey={'1'}>
                            <Collapse.Panel header={<Text>Stack trace</Text>} key="1">
                                <table>
                                    <tbody>
                                        {props.issue.stackTrace.map((frame, index) => {
                                            return (
                                                <tr
                                                    key={index}
                                                    style={{
                                                        verticalAlign: 'baseline',
                                                        borderBottomStyle: 'dashed',
                                                        borderBlockColor: antGrey.grey5,
                                                        borderBottomWidth: 1,
                                                    }}
                                                >
                                                    <td
                                                        style={{
                                                            paddingTop: 10,
                                                            paddingBottom: 10,
                                                            paddingLeft: 0,
                                                            paddingRight: 25,
                                                        }}
                                                    >
                                                        <Text
                                                            code
                                                            style={{
                                                                fontSize: 'smaller',
                                                            }}
                                                        >
                                                            {frame.package}
                                                        </Text>
                                                    </td>
                                                    <td
                                                        style={{
                                                            paddingTop: 10,
                                                            paddingBottom: 10,
                                                        }}
                                                    >
                                                        <Text
                                                            code
                                                            style={{
                                                                wordBreak: 'break-all',
                                                            }}
                                                        >
                                                            {frame.function}
                                                        </Text>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </Collapse.Panel>
                        </Collapse>
                    )}
                </Card>
            )}
        </Drawer>
    );
};

const CommitCard = ({
    commit,
    vcsId,
    use24HourTimeFormat,
    effectiveTimezone,
}: {
    commit: CommitItem;
    vcsId: IntegrationType;
    use24HourTimeFormat?: boolean;
    effectiveTimezone: string;
}) => {
    const commitCardStyle: React.CSSProperties = {
        backgroundColor: antGrey.grey3,
        borderRadius: 8,
        padding: 16,
        paddingBottom: 0,
    };
    return (
        <div
            style={{
                ...commitCardStyle,
            }}
        >
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    paddingBottom: 14,
                }}
            >
                <Paragraph
                    style={{
                        color: antGrey.grey8,
                        fontWeight: 600,
                        marginRight: 'auto',
                        marginBottom: 0,
                    }}
                    ellipsis={{ rows: 3 }}
                >
                    {commit.message}
                </Paragraph>
            </div>
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: 10,
                    padding: '6px 0px',
                    color: antGrey.grey8,
                    borderBottom: `1px solid ${antGrey.grey5}`,
                }}
            >
                <dt style={{ marginRight: 'auto', minWidth: '25%' }}>Commit hash</dt>
                <IntegrationIcon integrationId={vcsId} size={20} />
                <dd style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    <Link href={commit.url} target="_blank" style={{ whiteSpace: 'nowrap' }}>
                        {commit.identifier.substring(0, 7)}
                    </Link>
                </dd>
            </div>
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: 10,
                    padding: '12px 0px 6px',
                    color: antGrey.grey8,
                }}
            >
                <dt style={{ marginRight: 'auto' }}>Author</dt>
                <Avatar
                    style={{
                        fontSize: 10,
                        backgroundColor: getAvatarColorForName(commit.author),
                        flexShrink: 0,
                    }}
                    size={24}
                >
                    {commit.author.charAt(0).toUpperCase()}
                </Avatar>
                <dd>{commit.author}</dd>
            </div>
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: '12px 0px',
                    color: antGrey.grey8,
                    borderTop: `1px solid ${antGrey.grey5}`,
                }}
            >
                <dt>Commit date</dt>
                <dd>
                    {formatDateWithTimezone(
                        commit.committedAt,
                        'shortWithComma',
                        effectiveTimezone,
                        use24HourTimeFormat,
                    )}
                </dd>
            </div>
        </div>
    );
};

export default StabilityIssueDrawer;
